import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CoachState } from "@/providers/walter-coach";
import { CoachSmartCatalogPayload, SmartCatalogOptions, SmartCatalogOverridePayload } from "@/services/walter-assistant";
import { Briefcase, UserRoundSearch } from "lucide-react";
import { useMemo } from "react";
import { SmartCatalogEmptyState } from "./smart-catalog-empty-state";
import { SmartCatalogFilters } from "./smart-catalog-filters";
import { SmartCatalogHeader } from "./smart-catalog-header";
import { SmartCatalogSubTableGroupContent } from "./smart-catalog-sub-table-group-content";
import { SmartCatalogTheme } from "./smart-catalog-theme";
import { formatProfessionLabel, groupThemesBySubTable, shouldGroupThemesBySubTable } from "./smart-catalog.utils";

type Props = {
    payload: CoachSmartCatalogPayload | null;
    isPending: boolean;
    /** True quand Phase B (verbatims LLM) est en cours — indicateur visuel uniquement. */
    isVerbatimPending: boolean;
    onRefresh: () => void;
    coachState: CoachState;
    /**
     * Picklists des trois axes d'override (provider). `null` tant que
     * non chargées / CRM indisponible ⇒ on masque les filtres et on
     * dégrade sur la bannière "profession détectée".
     */
    options?: SmartCatalogOptions | null;
    /** Pose ou réinitialise un override d'axe (chaîne vide ⇒ reset). */
    onOverride?: (payload: SmartCatalogOverridePayload) => void;
    /**
     * Appelé avec les theme_codes nouvellement révélés quand l'utilisateur
     * clique sur "Voir plus de thèmes". Permet au provider de déclencher la
     * génération on-demand des verbatims pour les thèmes encore pending.
     */
    onRevealThemes?: (themeCodes: string[]) => void;
};

/**
 * Entrée principale du Smart Catalog. Affiche :
 * - L'en-tête (refresh + indicateur de fraîcheur).
 * - Les filtres d'override (toujours visibles en appel dès que les picklists
 *   sont chargées — indépendamment du pending ou du payload).
 * - La liste de thèmes, ordonnée par score décroissant (ordre imposé par
 *   le backend). Chaque thème porte une matrice de ses produits.
 *
 * Le rendu est entièrement piloté par les props — pas d'effet local,
 * pas de fetch. Le provider (`WalterCoachProvider`) est la seule
 * source de vérité.
 */
export function SmartCatalogPanel({ payload, isPending, isVerbatimPending, onRefresh, coachState, options, onOverride, onRevealThemes }: Props) {
    const themes = payload?.themes;
    const catalogOrigins = payload?.catalog_origins ?? [];
    const signals = payload?.prospect_signals ?? null;

    /*
     * Profession effective (override ?? détecté) telle qu'elle a servi au
     * scoring. On retombe sur la clé legacy `prospect_profession_code`
     * pour la back-compat avec les payloads pre-override.
     */
    const effectiveProfession = (signals?.effective_profession_code ?? signals?.prospect_profession_code ?? "").trim();

    /*
     * Filtres dès que les picklists sont disponibles ET qu'on est en appel
     * ou qu'un payload existe (post-call). Sans picklists on dégrade sur la
     * bannière "profession détectée".
     */
    const canRenderFilters = Boolean(options && onOverride && (coachState === "open_in_call" || payload));

    /*
     * Bannière "Profession détectée" — repli quand les filtres ne peuvent
     * pas se rendre (picklists indisponibles).
     */
    const detectedProfileBanner = useMemo(() => {
        if (canRenderFilters) return null;
        if (!effectiveProfession) return null;
        return formatProfessionLabel(effectiveProfession);
    }, [canRenderFilters, effectiveProfession]);

    /*
     * Gate "profession requise" : filtres visibles mais pas encore de
     * catalogue (garde santé initial ou première génération en attente).
     */
    const showProfessionGate = canRenderFilters && !payload && coachState === "open_in_call";

    const showPreCallEmpty = !payload && !isPending && coachState !== "open_in_call";
    const showLoadingSkeleton = !payload && isPending && coachState === "open_in_call";
    const showInCallEmpty = !payload && !isPending && coachState === "open_in_call";

    const qualificationNotice = payload && catalogOrigins.length === 0 ? "Profil en cours de qualification" : null;

    const themeGroups = useMemo(() => {
        if (!themes?.length || !shouldGroupThemesBySubTable(themes)) return null;
        const groups = groupThemesBySubTable(themes, catalogOrigins);
        return groups.length > 0 ? groups : null;
    }, [themes, catalogOrigins]);

    const inCallContent = coachState === "open_in_call" || payload;

    return (
        <div className="flex h-full min-h-0 flex-col gap-4">
            <SmartCatalogHeader payload={payload} isPending={isPending} isVerbatimPending={isVerbatimPending} onRefresh={onRefresh} />

            {showPreCallEmpty ? (
                <div className="min-h-0 flex-1">
                    <SmartCatalogEmptyState variant="pre_call" />
                </div>
            ) : null}

            {inCallContent ? (
                <div className="flex min-h-0 flex-1 flex-col gap-5">
                    {canRenderFilters ? (
                        <SmartCatalogFilters signals={signals} options={options!} isPending={isPending} onOverride={onOverride!} />
                    ) : detectedProfileBanner ? (
                        <div role="status" aria-live="polite" className="flex items-center gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                            <Briefcase className="size-4 shrink-0" aria-hidden />
                            <span>
                                <span className="text-blue-700">Profession détectée :</span> <strong className="font-semibold">{detectedProfileBanner}</strong>
                            </span>
                        </div>
                    ) : null}

                    {showProfessionGate ? (
                        <div className="flex items-start gap-3 rounded-md border border-dashed border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500">
                            <UserRoundSearch className="mt-0.5 size-4 shrink-0 text-gray-400" aria-hidden />
                            <span>Sélectionnez une profession pour afficher le catalogue.</span>
                        </div>
                    ) : null}

                    {showLoadingSkeleton ? <SmartCatalogEmptyState variant="loading_no_payload" /> : null}

                    {showInCallEmpty && !showProfessionGate ? (
                        <SmartCatalogEmptyState variant="in_call_empty" />
                    ) : null}

                    {payload && themes ? (
                        <div key={payload.generated_at_ms} className="flex flex-col gap-5 duration-200 animate-in fade-in">
                            {qualificationNotice ? <p className="text-sm text-gray-500">{qualificationNotice}</p> : null}

                            {themes.length > 0 ? (
                                themeGroups ? (
                                    <Tabs defaultValue={themeGroups[0].subTable}>
                                        <TabsList className="w-fit justify-start">
                                            {themeGroups.map(group => (
                                                <TabsTrigger key={group.subTable} value={group.subTable} className="gap-2">
                                                    Catalogue {group.label}
                                                    <span className="text-xs font-normal text-muted-foreground">
                                                        {group.themes.length} thème{group.themes.length > 1 ? "s" : ""}
                                                    </span>
                                                </TabsTrigger>
                                            ))}
                                        </TabsList>
                                        {themeGroups.map(group => (
                                            <TabsContent key={group.subTable} value={group.subTable} className="mt-3">
                                                <SmartCatalogSubTableGroupContent
                                                    themes={group.themes}
                                                    onRevealThemes={onRevealThemes ? newThemes => onRevealThemes(newThemes.map(t => t.theme_code)) : undefined}
                                                />
                                            </TabsContent>
                                        ))}
                                    </Tabs>
                                ) : (
                                    <div className="flex flex-col gap-3">
                                        {themes.map(theme => (
                                            <SmartCatalogTheme key={theme.theme_code} theme={theme} />
                                        ))}
                                    </div>
                                )
                            ) : (
                                <p className="rounded-md border border-dashed border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500">Aucun thème éligible pour l&apos;instant.</p>
                            )}
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
